<script setup lang="ts">
  import { ref, watch } from 'vue'
  import type { LoginResponse } from '@/types/login-types.ts'
  import axios from 'axios'
  import UsernamePassword from '@/components/login/UsernamePassword.vue'
  import EmailCode from '@/components/login/EmailCode.vue'
  import AuthenticatorCode from '@/components/login/AuthenticatorCode.vue'
  import { useRoute, useRouter } from 'vue-router'
  import { validateEmailOrUsername } from '@/utils/validation.ts'
  import { useLoginStore } from '@/store/login.store.ts'
  import { parseJwt } from '@/utils/jwt.utils'
  import { getToken } from '@/utils/axios-client'
  import { require } from '@logistiek-zonder-papier/lzp-library'

  const router = useRouter()
  const route = useRoute()
  const loginStore = useLoginStore()

  const form = ref<{
    username: string
    password: string
    code: string
  }>({
    username: '',
    password: '',
    code: '',
  })

  const errorMessage = ref<{
    general?: string | undefined
    username?: string | undefined
    password?: string | undefined
  }>({})
  const loginResponse = ref<LoginResponse | null>(null)
  const isSubmitting = ref<boolean>(false)

  const defaultRedirect = {
    nestjs: '/',
    idf: '/home',
  } as const

  watch(
    () => form.value.username,
    () => {
      errorMessage.value.username = undefined
      errorMessage.value.general = undefined
    }
  )

  watch(
    () => form.value.password,
    () => {
      errorMessage.value.password = undefined
      errorMessage.value.general = undefined
    }
  )

  async function submitUsernamePassword() {
    isSubmitting.value = true

    errorMessage.value.general = undefined
    errorMessage.value.username = undefined
    errorMessage.value.password = undefined

    let valid = true

    if (!validateEmailOrUsername(form.value.username)) {
      errorMessage.value.username = 'Vul een valide gebruikersnaam in'
      valid = false
    }

    if (form.value.password.length === 0) {
      errorMessage.value.password = 'Een wachtwoord is verplicht'
      valid = false
    } else if (form.value.password.length > 512) {
      errorMessage.value.password = 'Een wachtwoord kan niet meer dan 512 tekens zijn'
      valid = false
    }

    if (!valid) {
      isSubmitting.value = false
      return
    }

    try {
      loginResponse.value = await loginStore.login(form.value.username, form.value.password)
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw error
      }
      errorMessage.value.general = 'Het verifiëren van de inloggegevens is mislukt'
    }
    isSubmitting.value = false
  }

  async function submitMFACode() {
    if (!loginResponse.value) return
    isSubmitting.value = true

    errorMessage.value.general = undefined
    errorMessage.value.username = undefined
    errorMessage.value.password = undefined

    try {
      await loginStore.verifyMfa(
        loginResponse.value.token,
        form.value.code,
        loginResponse.value.authenticationType
      )
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw error
      }
      errorMessage.value.general = error.response?.data.message || error.response?.data.detail
      isSubmitting.value = false
      return
    }

    let redirect = route.query['redirect']?.toString()

    const environment: 'nestjs' | 'idf' | undefined = parseJwt(require(getToken()))['environment']
    const newEnvironment = environment == 'nestjs'

    if (redirect?.startsWith('/home') && !newEnvironment) {
      redirect = undefined
    }

    await router.replace(redirect ?? defaultRedirect[environment ?? 'idf'])
  }
</script>

<template>
  <div class="login-layout">
    <div class="login-card">
      <section class="content-layout">
        <img
          id="logo"
          class="logo"
          src="/content/img/lzp_logo.png"
          width="200"
          height="132"
          alt="LZP logo"
        />
        <div class="login-form">
          <UsernamePassword
            v-if="loginResponse === null"
            v-model:username.trim="form.username"
            v-model:password.trim="form.password"
            :isSubmitting="isSubmitting"
            :errorMessage="errorMessage"
            @submit="submitUsernamePassword"
          />
          <EmailCode
            v-else-if="loginResponse.authenticationType === 'MAIL'"
            v-model.trim="form.code"
            :isSubmitting="isSubmitting"
            :errorMessage="errorMessage.general"
            @submit="submitMFACode"
          />
          <AuthenticatorCode
            v-else-if="loginResponse.authenticationType === 'AUTHENTICATOR'"
            v-model.trim="form.code"
            :setupUri="loginResponse.authenticatorSetupUri"
            :isSubmitting="isSubmitting"
            :errorMessage="errorMessage.general"
            @submit="submitMFACode"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
  .logo {
    align-self: center;
  }

  .login-layout {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 100vh;
  }

  .login-card {
    border: 1px solid var(--colours-grey-3);
    border-radius: var(--components-card-and-modal-corners);
    background-color: var(--colours-grey-1);
    display: flex;
    width: var(--layout-8-col-text-max);
    padding: 0 var(--layout-side-padding-desktop);
    flex-direction: column;
  }

  .content-layout {
    display: flex;
    padding: var(--spacing-l) 0;
    flex-direction: column;
    gap: var(--spacing-s);
  }

  .login-form {
    display: flex;
    padding: var(--spacing-m);
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-s);
  }
</style>
